import {
  Button,
  Divider,
  DropzoneReadonly,
  FiFileText,
  FiSearch,
  Flex,
  FormProvider,
  Grid,
  GridItem,
  SelectField,
  Stack,
  Text,
  TextField,
} from '@agrotoken-tech/ui';
import { useTranslation } from '@bcba/react-hooks';
import { useUpsertProductiveFacility } from './useUpsertProductiveFacility';
import { FileMimeType, ownershipTypes } from '@bcba/types';
import { downloadDocument } from '../../../../../utils/downloadDocument.utils';
import { CommonProductiveFacilitySerializer } from '@bcba/serializers';
import { usePendingChangesAlert } from '../../../../../contexts/hooks';
import { S3FileUploader } from '@bcba/frontend-utils';
import { getS3SignedUrl } from '../../../../../services/documents/getS3SignedUrl';

const formId = 'upsert-productive-facility';

interface Props {
  onSuccess: () => void;
  onCancel: () => void;
  selectedFacility?: CommonProductiveFacilitySerializer;
}

export const UpsertProductiveFacility = ({
  onSuccess,
  onCancel,
  selectedFacility,
}: Props) => {
  const { t } = useTranslation();

  const {
    formMethods,
    onSubmit,
    provincesOptions,
    hasSelectedProvince,
    getProvinceLocalitiesOptions,
    isPendingUpsertFacility,
  } = useUpsertProductiveFacility({
    onSuccess,
    selectedFacility,
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isDirty },
    resetField,
  } = formMethods;
  usePendingChangesAlert(isDirty);

  const ownershipOptions = ownershipTypes.map((ot) => ({
    label: t(`ownershipType.${ot}`),
    value: ot,
  }));

  const isUpdating = Boolean(selectedFacility);

  const sectionTitle = isUpdating
    ? t('explorer.productiveFacilities.editFacility')
    : t('explorer.productiveFacilities.addFacility');
  const confirmLabel = isUpdating ? t('common.save') : t('common.add');

  return (
    <Stack divider={<Divider />} gap={4}>
      <Text size={'xl'}>{sectionTitle}</Text>
      <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormProvider {...formMethods}>
          <Stack gap={8}>
            <Grid templateColumns={{ md: 'repeat(2, 1fr)' }} gap={6}>
              <TextField
                name={'name'}
                label={t('productiveFacilities.upsert.establishmentName.label')}
                placeholder={t(
                  'productiveFacilities.upsert.establishmentName.placeholder',
                )}
                errorMessageFormatter={t}
                rhfOptions={{ required: true }}
              />
              <SelectField
                name={'ownershipType'}
                label={t('productiveFacilities.upsert.ownership.label')}
                placeholder={t(
                  'productiveFacilities.upsert.ownership.placeholder',
                )}
                options={ownershipOptions}
                errorMessageFormatter={t}
                rhfOptions={{ required: true }}
              />
              {Boolean(provincesOptions?.length) && (
                <>
                  <SelectField
                    name="provinceUuid"
                    label={t('productiveFacilities.upsert.province.label')}
                    placeholder={t(
                      'productiveFacilities.upsert.province.placeholder',
                    )}
                    errorMessageFormatter={t}
                    options={provincesOptions}
                    defaultInputValue={selectedFacility?.province?.name}
                    rhfOptions={{ required: true }}
                  />

                  <SelectField
                    name="localityUuid"
                    label={t('productiveFacilities.upsert.locality.label')}
                    placeholder={t(
                      'productiveFacilities.upsert.locality.placeholder',
                    )}
                    errorMessageFormatter={t}
                    options={
                      selectedFacility
                        ? [
                            {
                              label: selectedFacility?.locality?.name,
                              value: selectedFacility?.locality?.uuid,
                            },
                          ]
                        : []
                    }
                    isDisabled={!hasSelectedProvince}
                    leftIcon={FiSearch}
                    debounce={500}
                    getOptions={getProvinceLocalitiesOptions}
                    noOptionMessage={t(
                      'productiveFacilities.upsert.locality.noOptionsMessage',
                    )}
                    isClearable
                    cacheOptions={false}
                    defaultInputValue={selectedFacility?.locality.name}
                    rhfOptions={{ required: true }}
                  />
                </>
              )}
              <GridItem colSpan={{ base: 1, md: 2 }}>
                {watch('geolocationDocument.uuid') ? (
                  <DropzoneReadonly
                    label={t(
                      'productiveFacilities.upsert.geolocationDocument.label',
                    )}
                    fileName={
                      selectedFacility?.geolocationDocument?.originalFileName ??
                      '-'
                    }
                    onClick={() =>
                      downloadDocument(
                        selectedFacility?.geolocationDocument?.uuid ?? '',
                      )
                    }
                    onDelete={() =>
                      setValue('geolocationDocument.uuid', undefined, {
                        shouldDirty: true,
                      })
                    }
                    leftIcon={FiFileText}
                  />
                ) : (
                  <S3FileUploader
                    fieldPrefix="geolocationDocument.file.s3TempKey"
                    label={t(
                      'productiveFacilities.upsert.geolocationDocument.label',
                    )}
                    getS3PreSignedUrl={getS3SignedUrl}
                    onFileUploadSuccess={async (
                      fileName,
                      mimeType,
                      s3TempKey,
                    ) => {
                      setValue('geolocationDocument.file.name', fileName);
                      setValue(
                        'geolocationDocument.file.mimeType',
                        mimeType as FileMimeType,
                      );
                      setValue('geolocationDocument.file.s3TempKey', s3TempKey);
                      setValue('geolocationDocument.keepOriginal', false, {
                        shouldDirty: true,
                      });
                    }}
                    onDeleteClick={() => {
                      resetField('geolocationDocument.file.s3TempKey');
                      resetField('geolocationDocument.file.name');
                      resetField('geolocationDocument.file.mimeType');
                    }}
                    required
                  />
                )}
              </GridItem>
            </Grid>
            <Flex justifyContent={'end'} gap={4}>
              <Button variant={'secondary'} onClick={onCancel}>
                {t('common.cancel')}
              </Button>
              <Button
                variant={'primary'}
                type={'submit'}
                isLoading={isPendingUpsertFacility}
                isDisabled={!isDirty}
              >
                {confirmLabel}
              </Button>
            </Flex>
          </Stack>
        </FormProvider>
      </form>
    </Stack>
  );
};
